import { Col, Empty, Row, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMemo } from 'react';
import ReactECharts, { AXIS_TEXT, GRID_LINE } from '../charts/echarts';
import type { OverseasTopRow, OverviewRegion, OverviewResponse } from '../contract';
import { creatorName, fmtCompact, fmtDay, fmtKc, fmtNum, fmtShare } from '../format';
import { rowOpenProps } from './row-open';
import styles from '../style.module.scss';
import more from '../style-more.module.scss';

const LINE = '#1F5FA8';

/** Đường KC quốc tế theo ngày (chỉ khi tháng giữ chuỗi ngày). */
const OverseasLine = ({ days, values }: { days: string[]; values: number[] }) => {
  const option = useMemo(
    () => ({
      animation: false,
      grid: { left: 8, right: 16, top: 12, bottom: 8, containLabel: true },
      tooltip: { trigger: 'axis', valueFormatter: (v: number) => `${fmtKc(v)} KC` },
      xAxis: {
        type: 'category',
        data: days.map(fmtDay),
        boundaryGap: false,
        axisLine: { lineStyle: { color: GRID_LINE } },
        axisLabel: { color: AXIS_TEXT },
      },
      yAxis: {
        type: 'value',
        min: 0,
        splitLine: { lineStyle: { color: GRID_LINE } },
        axisLabel: { color: AXIS_TEXT, formatter: (v: number) => fmtCompact(v) },
      },
      series: [
        {
          name: 'KC quốc tế',
          type: 'line',
          data: values,
          smooth: true,
          symbolSize: 5,
          lineStyle: { width: 2, color: LINE },
          itemStyle: { color: LINE },
          areaStyle: { color: LINE, opacity: 0.08 },
        },
      ],
    }),
    [days, values]
  );
  return <ReactECharts option={option} style={{ height: 180, width: '100%' }} />;
};

export const regionLabel = (r: { flag: string; name: string }) => `${r.flag} ${r.name}`;

const regionColumns: ColumnsType<OverviewRegion> = [
  {
    title: 'Khu vực',
    key: 'region',
    render: (_, r) => <span className={more.region}>{regionLabel(r)}</span>,
  },
  {
    title: 'Creator có trong top 3',
    dataIndex: 'creators',
    align: 'right',
    width: 120,
    render: (v: number) => fmtNum(v),
  },
  {
    title: '#1 ở',
    dataIndex: 'firstCount',
    align: 'right',
    width: 110,
    render: (v: number) => `${fmtNum(v)} Creator`,
  },
];

interface Props {
  ov: OverviewResponse;
  onSelect: (uid: string) => void;
}

/** Khối "KC từ nước ngoài" (đặc tả 04 §5): tổng + %, đường theo ngày, xếp khu vực, Top 10 Creator. */
const OverseasSection = ({ ov, onSelect }: Props) => {
  const o = ov.overseas;
  if (!o) return null;
  const topColumns: ColumnsType<OverseasTopRow> = [
    {
      title: 'Creator',
      key: 'creator',
      render: (_, r) => (
        <div className={styles.cellCreator}>
          <span className={styles.cellName}>{creatorName(r)}</span>
          {r.groupName && <span className={styles.cellGroup}>{r.groupName}</span>}
        </div>
      ),
    },
    {
      title: 'KC quốc tế',
      dataIndex: 'overseasDiamonds',
      align: 'right',
      width: 120,
      render: (v: number) => <strong>{fmtKc(v)}</strong>,
    },
    {
      title: '% KC của họ',
      dataIndex: 'sharePct',
      align: 'right',
      width: 100,
      render: (v: number | null) => fmtShare(v),
    },
    {
      title: 'Khu vực',
      key: 'regions',
      width: 170,
      render: (_, r) => (r.regions.length ? r.regions.join(', ') : '—'),
    },
  ];

  return (
    <section className={`${styles.card} ${styles.section}`} aria-label="KC từ nước ngoài">
      <h2 className={styles.cardTitle}>KC từ nước ngoài</h2>
      <div className={more.statLine}>
        <span>
          <strong className={more.statBig}>{fmtKc(o.diamonds)}</strong> KC
        </span>
        <span>
          <strong>{fmtShare(o.sharePct)}</strong> tổng KC của phạm vi
        </span>
        <span>
          <strong>{fmtNum(o.creators)}</strong> Creator có KC quốc tế
        </span>
      </div>
      {ov.seriesAvailable && o.series.length > 1 && (
        <OverseasLine days={ov.series.days} values={o.series} />
      )}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={10}>
          <h3 className={more.subTitle}>Khu vực khách quốc tế</h3>
          {o.regions.length ? (
            <Table<OverviewRegion>
              size="small"
              rowKey="code"
              columns={regionColumns}
              dataSource={o.regions}
              pagination={false}
              scroll={{ x: 360 }}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có khu vực nào." />
          )}
          <p className={more.note}>
            TikTok chỉ cho thứ hạng khu vực (top 3 của từng Creator), không cho số KC từng nước.
          </p>
        </Col>
        <Col xs={24} lg={14}>
          <h3 className={more.subTitle}>Top 10 Creator theo KC quốc tế</h3>
          {o.top.length ? (
            <Table<OverseasTopRow>
              size="small"
              rowKey="uid"
              columns={topColumns}
              dataSource={o.top}
              pagination={false}
              scroll={{ x: 520 }}
              rowClassName={() => styles.clickRow}
              onRow={(r) => rowOpenProps(`Xem chi tiết ${creatorName(r)}`, () => onSelect(r.uid))}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Chưa có Creator có KC quốc tế."
            />
          )}
        </Col>
      </Row>
    </section>
  );
};

export default OverseasSection;
